import { parseRigheAbs } from "./tableViewerUtil";

export function expandSectorRemoval(positionsToRemove, sourceItems, activeTable, axis, KERF = 4.4) {
  if (!activeTable) {
    return positionsToRemove;
  }

  const cascadeAxis = axis;

  if (!cascadeAxis) {
    return positionsToRemove;
  }

  const crossAxis = cascadeAxis === "x" ? "y" : "x";
  const tolerance = Math.max(KERF * 2, 2);

  const activeTrim = Number(activeTable.trim ?? activeTable.TRIM ?? 10);

  const [tableH, tableW] = JSON.parse(activeTable.size || "[0,0]");

  const cascadeLimit = cascadeAxis === "x" ? tableW : tableH;

  const crossLimit = crossAxis === "x" ? tableW : tableH;

  const getStart = (entry, axis) => Number(entry.position[axis]) || 0;

  const getSize = (entry, axis) =>
    Number(entry.position[axis === "x" ? "width" : "height"]) || 0;

  const getEnd = (entry, axis) =>
    getStart(entry, axis) + getSize(entry, axis);

  const getCenter = (entry, axis) =>
    getStart(entry, axis) + getSize(entry, axis) / 2;

  const entries = (Array.isArray(sourceItems) ? sourceItems : []).flatMap(
    (item) =>
      (item.processedPositions || [])
        .filter(
          (position) => String(position.id) === String(activeTable.id)
        )
        .map((position) => ({
          itemId: item.itemId,
          instanceId: position.instanceId,
          position
        }))
  );

  const entriesByKey = new Map(
    entries.map((entry) => [
      `${String(entry.itemId)}::${String(entry.instanceId)}`,
      entry
    ])
  );

  /*
   * Fontos: itt már az aktuális workingTable kerül átadásra,
   * nem a callback által bezárt régi table prop.
   */
  const { horizontals, verticals } = parseRigheAbs(activeTable);

  const sectorBoundaryLines =
    crossAxis === "x"
      ? verticals
          .filter(
            (line) =>
              Number(line.y1) <= activeTrim + tolerance &&
              Number(line.y2) >= cascadeLimit - activeTrim - tolerance
          )
          .map((line) => Number(line.x))
      : horizontals
          .filter(
            (line) =>
              Number(line.x1) <= activeTrim + tolerance &&
              Number(line.x2) >= cascadeLimit - activeTrim - tolerance
          )
          .map((line) => Number(line.y));

  const getSectorBounds = (anchorEntry) => {
    const anchorCenter = getCenter(anchorEntry, crossAxis);

    const boundaries = [
      activeTrim,
      ...sectorBoundaryLines,
      crossLimit - activeTrim
    ]
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    let sectorStart = activeTrim;
    let sectorEnd = crossLimit - activeTrim;

    for (const boundary of boundaries) {
      if (boundary <= anchorCenter + tolerance) {
        sectorStart = boundary;
      }

      if (boundary > anchorCenter + tolerance) {
        sectorEnd = boundary;
        break;
      }
    }

    // Partial cuts bound independent child sectors too. Only use cuts
    // spanning the whole anchor, so a neighbouring child's cut cannot
    // accidentally split its parent sector.
    let cascadeStart = activeTrim;
    let cascadeEnd = cascadeLimit - activeTrim;
    const anchorStart = getStart(anchorEntry, cascadeAxis);
    const anchorEnd = getEnd(anchorEntry, cascadeAxis);
    const lines = crossAxis === "x" ? verticals : horizontals;
    for (const line of lines) {
      const coordinate = Number(crossAxis === "x" ? line.x : line.y);
      const from = Number(crossAxis === "x" ? line.y1 : line.x1);
      const to = Number(crossAxis === "x" ? line.y2 : line.x2);
      if (from > anchorStart + 0.01 || to < anchorEnd - 0.01) continue;
      const leftBoundary = coordinate <= getStart(anchorEntry, crossAxis) + 0.01 &&
        coordinate >= sectorStart - 0.01;
      const rightBoundary = coordinate >= getEnd(anchorEntry, crossAxis) - 0.01 &&
        coordinate <= sectorEnd + 0.01;
      if (!leftBoundary && !rightBoundary) continue;
      if (leftBoundary) sectorStart = coordinate;
      if (rightBoundary) sectorEnd = coordinate;
      cascadeStart = Math.max(cascadeStart, from);
      cascadeEnd = Math.min(cascadeEnd, to);
    }
    return {
      start: sectorStart, end: sectorEnd, cascadeStart, cascadeEnd
    };
  };

  const buildLogicalSectorOrder = (anchorEntry) => {
    const sectorBounds = getSectorBounds(anchorEntry);

    const sectorEntries = entries.filter((entry) => {
      const center = getCenter(entry, crossAxis);

      return (
        center >= sectorBounds.start - tolerance &&
        center <= sectorBounds.end + tolerance &&
        getStart(entry, crossAxis) >= sectorBounds.start - 0.01 &&
        getEnd(entry, crossAxis) <= sectorBounds.end + 0.01 &&
        getStart(entry, cascadeAxis) >= sectorBounds.cascadeStart - 0.01 &&
        getStart(entry, cascadeAxis) <= sectorBounds.cascadeEnd + 0.01
      );
    });

    const lanes = [];

    const sortedEntries = [...sectorEntries].sort(
      (a, b) => getStart(a, crossAxis) - getStart(b, crossAxis)
    );

    sortedEntries.forEach((entry) => {
      const entryStart = getStart(entry, crossAxis);
      const entryEnd = getEnd(entry, crossAxis);
      const entrySize = entryEnd - entryStart;

      const matchingLane = lanes.find((lane) => {
        const overlap =
          Math.min(lane.end, entryEnd) - Math.max(lane.start, entryStart);

        const requiredOverlap =
          Math.min(lane.end - lane.start, entrySize) * 0.7;

        return overlap > 0 && overlap + tolerance >= requiredOverlap;
      });

      if (matchingLane) {
        matchingLane.entries.push(entry);

        matchingLane.start = Math.min(matchingLane.start, entryStart);

        matchingLane.end = Math.max(matchingLane.end, entryEnd);
      } else {
        lanes.push({
          start: entryStart,
          end: entryEnd,
          entries: [entry]
        });
      }
    });

    lanes.sort((a, b) => a.start - b.start);

    return lanes.flatMap((lane, laneIndex) => {
      const laneEntries = [...lane.entries];
      const forward = laneIndex % 2 === 0;

      laneEntries.sort((a, b) => {
        if (forward) {
          return getStart(a, cascadeAxis) - getStart(b, cascadeAxis);
        }

        return getEnd(b, cascadeAxis) - getEnd(a, cascadeAxis);
      });

      return laneEntries;
    });
  };

  const result = new Map();

  positionsToRemove.forEach((selection) => {
    const key =
      `${String(selection.itemId)}::` + `${String(selection.instanceId)}`;

    result.set(key, selection);
  });

  positionsToRemove.forEach((selection) => {
    const anchorKey =
      `${String(selection.itemId)}::` + `${String(selection.instanceId)}`;

    const anchorEntry = entriesByKey.get(anchorKey);

    if (!anchorEntry) return;

    const logicalOrder = buildLogicalSectorOrder(anchorEntry);

    const anchorIndex = logicalOrder.findIndex(
      (entry) =>
        String(entry.itemId) === String(anchorEntry.itemId) &&
        String(entry.instanceId) === String(anchorEntry.instanceId)
    );

    if (anchorIndex < 0) return;

    const laterEntries = new Set([
      ...logicalOrder.slice(anchorIndex + 1),
      ...logicalOrder.filter(
        (entry) =>
          getStart(entry, cascadeAxis) >=
          getEnd(anchorEntry, cascadeAxis) - 0.01
      )
    ]);

    laterEntries.forEach((entry) => {
      const key =
        `${String(entry.itemId)}::` + `${String(entry.instanceId)}`;

      result.set(key, {
        itemId: entry.itemId,
        instanceId: entry.instanceId
      });
    });
  });

  return Array.from(result.values());
}
